/* eslint-disable react/style-prop-object */

import React, {
  type ComponentProps,
  memo,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import * as Clipboard from 'expo-clipboard';
import { ImpactFeedbackStyle } from 'expo-haptics';
import { type Href, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';

import { type Asset } from 'contentful';

import {
  ProductCardV2,
  triggerHapticFeedback,
  useHovered,
  useNoticeBannersStackActions,
} from '@sg/garnish';
import { useProductMicroNutritionData } from '@sg/shared-fe-features-menu';
import { ProductDetails } from '@sg/shared-fe-features-product-details';

import { MenuCategoriesGrid } from '@order/components-menu';
import { ProductDetailsCardCarousel } from '@order/features-product-details';

import { type useOneClickAddToBag } from '../../../../hooks/useOneClickAddToBag';
import { type MenuContentProduct } from '../../MenuContent.types';
import { MenuProductCardLineageOverlay } from './components';
import { useProductDetailsCarouselItems } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentProductCard = memo((props: MenuContentProductCardProps) => {
  const {
    product,
    imageFetchingPriority,
    hoverImageUrl,
    hoverStaticImageUrl,
    hoverImageAlt,
    carousel,
    hasProductDetails,
    generateHref,
    calorieOverride,
    calorieOverrideText,
    isLineageKeyCopyModeEnabled = false,
    restrictionNotice,
    addProductToBag,
    isAddingProductToBag,
    onCardPress,
    onProductDetailsLinkPress,
  } = props;

  const { baseProduct, isModifiable, name, description, outOfStock, asset, label, cost, slug } =
    product;

  const { fontScale } = useWindowDimensions();
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { clear: clearNoticeBanners, push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Refs ────────────────────────────────────────────────────────────

  const cardContainerRef = useRef<View>(null);
  const isHovered = useHovered(cardContainerRef);

  // ─── Derived Data ────────────────────────────────────────────────────

  const href = useMemo(() => generateHref(slug), [slug, generateHref]);

  const productDetailsHref = baseProduct?.slug ? `/product-details/${baseProduct.slug}` : '';

  const isLineageKeyAvailable = Boolean(baseProduct.lineageKey);
  const shouldShowLineageKeyCopyHover = isLineageKeyCopyModeEnabled && isHovered;

  const { carouselItems } = useProductDetailsCarouselItems({
    carousel,
    productAssetUrl: product.asset.url,
    productName: product.name,
  });

  // ─── Nutrition Data ──────────────────────────────────────────────────

  const baseProductWithNutritionData = useMemo(() => {
    if (typeof calorieOverride === 'number') {
      return { ...baseProduct, calories: calorieOverride };
    }

    return baseProduct;
  }, [baseProduct, calorieOverride]);

  const macroNutritionData = useProductMicroNutritionData({
    baseProduct: baseProductWithNutritionData,
  });
  const nutritionDataEntriesLineBreak = fontScale >= 1.3 ? 2 : undefined;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isProductAvailable = !outOfStock;
  const isProductSupportsOneClickAddToBag = !isModifiable;
  const isNavigationEnabled = isProductAvailable && !isProductSupportsOneClickAddToBag;

  /**
   * Customers can add products to their bags with a single click by pressing
   * the entire product card or the standard 1-click CTA button.
   */
  const shouldUsePressableVariation = isProductAvailable && isProductSupportsOneClickAddToBag;

  const shouldRenderMacroNutritionData = macroNutritionData.length > 0;
  const shouldRenderProductDetailsLink = hasProductDetails && baseProduct?.slug;

  // NOTE: On the web platform, we don't show the carousel in favor of hover image
  const shouldRenderCarousel = Platform.OS !== 'web' && carouselItems.length > 0;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOneClickAddToBag = useCallback(async () => {
    await addProductToBag(product, 1);
  }, [addProductToBag, product]);

  const trackCardPress = useCallback(() => {
    onCardPress(product);
  }, [onCardPress, product]);

  const trackProductDetailsLinkPress = useCallback(() => {
    onProductDetailsLinkPress(product);
  }, [onProductDetailsLinkPress, product]);

  const handleCarouselSlidePress = useCallback(() => {
    if (shouldUsePressableVariation) {
      void handleOneClickAddToBag();

      return;
    }

    if (isNavigationEnabled) {
      void triggerHapticFeedback(ImpactFeedbackStyle.Light);
      router.navigate(href);
      trackCardPress();
    }
  }, [
    shouldUsePressableVariation,
    handleOneClickAddToBag,
    isNavigationEnabled,
    href,
    router,
    trackCardPress,
  ]);

  const handleCopyLineageKey = useCallback(async () => {
    const { lineageKey } = baseProduct;

    if (!lineageKey) return;

    const hasCopied = await Clipboard.setStringAsync(lineageKey);

    if (!hasCopied) return;

    clearNoticeBanners();
    addNoticeBanner({
      id: 'menu-lineage-key-copy-success',
      palette: 'success',
      text: formatMessage(messages.lineageKeyCopied, { product_name: name }),
      highlights: [name],
      autoHideTimeout: 2000,
    });
  }, [addNoticeBanner, baseProduct, clearNoticeBanners, formatMessage, name]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View ref={cardContainerRef} style={styles.cardContainer}>
      {shouldRenderProductDetailsLink ? (
        <ProductDetails.Link
          href={productDetailsHref}
          name={product.name}
          slug={baseProduct.slug}
          onPress={trackProductDetailsLinkPress}
        />
      ) : null}

      <MenuContentProductCardContainer
        href={href}
        onPress={trackCardPress}
        onQuickAddToBag={handleOneClickAddToBag}
        isLoading={isAddingProductToBag}
        isNavigationEnabled={isNavigationEnabled}
        productName={name}
        variation={shouldUsePressableVariation ? 'pressable' : 'link'}
      >
        <ProductCardV2.Container
          canHover={isNavigationEnabled || shouldUsePressableVariation}
          style={styles.container}
        >
          {!shouldRenderCarousel && hoverImageUrl ? (
            <ProductCardV2.HoverImage
              source={hoverImageUrl}
              staticSource={hoverStaticImageUrl}
              alt={hoverImageAlt}
            />
          ) : null}

          {!shouldRenderCarousel && asset.url ? (
            <ProductCardV2.Image
              source={asset.url}
              alt={name}
              isUnavailable={outOfStock}
              priority={imageFetchingPriority}
            />
          ) : null}

          {shouldRenderCarousel ? (
            <ProductDetailsCardCarousel
              items={carouselItems}
              onSlidePress={isProductAvailable ? handleCarouselSlidePress : undefined}
            />
          ) : null}

          <ProductCardV2.LabelContainer>
            {isProductAvailable && label?.name ? (
              <ProductCardV2.Label>{label.name}</ProductCardV2.Label>
            ) : null}

            {isProductAvailable ? null : (
              <ProductCardV2.UnavailableLabel>
                <FormattedMessage {...messages.unavailableProductLabel} />
              </ProductCardV2.UnavailableLabel>
            )}
          </ProductCardV2.LabelContainer>

          <ProductCardV2.Heading>{name}</ProductCardV2.Heading>
          <ProductCardV2.Description>{description}</ProductCardV2.Description>

          {shouldRenderMacroNutritionData ? (
            <ProductCardV2.NutritionData.Stack lineBreakAfter={nutritionDataEntriesLineBreak}>
              {macroNutritionData.map((entry) => (
                <ProductCardV2.NutritionData.Item
                  key={entry.label}
                  value={entry.value}
                  label={entry?.labelShort ?? entry.label}
                />
              ))}
            </ProductCardV2.NutritionData.Stack>
          ) : null}

          {calorieOverrideText ? (
            <ProductCardV2.Notice style={styles.notice}>{calorieOverrideText}</ProductCardV2.Notice>
          ) : null}

          <ProductCardV2.Footer>
            <ProductCardV2.ProductDetailsContainer>
              <ProductCardV2.ProductDetail palette="dark-kale">
                <FormattedNumber value={cost / 100} style="currency" currency="USD" />
              </ProductCardV2.ProductDetail>
            </ProductCardV2.ProductDetailsContainer>

            {restrictionNotice ? (
              <ProductCardV2.AllergenNotice>{restrictionNotice}</ProductCardV2.AllergenNotice>
            ) : null}
          </ProductCardV2.Footer>
        </ProductCardV2.Container>
      </MenuContentProductCardContainer>

      {isProductSupportsOneClickAddToBag ? (
        <ProductCardV2.AddToBagContainer>
          <ProductCardV2.AddToBagIcon
            isLoading={isAddingProductToBag}
            isDisabled={!isProductAvailable}
          />
        </ProductCardV2.AddToBagContainer>
      ) : null}

      {shouldShowLineageKeyCopyHover ? (
        <MenuProductCardLineageOverlay
          isLineageKeyAvailable={isLineageKeyAvailable}
          productName={product.name}
          onPress={handleCopyLineageKey}
        />
      ) : null}
    </View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const MenuContentProductCardContainer = (props: MenuContentProductCardContainerProps) => {
  const {
    variation,
    href,
    productName,
    isNavigationEnabled,
    isLoading,
    children,
    onPress,
    onQuickAddToBag,
  } = props;

  if (variation === 'pressable') {
    return (
      <MenuCategoriesGrid.ProductCardPressable
        onPress={onQuickAddToBag}
        isDisabled={isLoading}
        isLoading={isLoading}
        productName={productName}
      >
        {children}
      </MenuCategoriesGrid.ProductCardPressable>
    );
  }

  return (
    <MenuCategoriesGrid.ProductCardLink
      href={href}
      onPress={onPress}
      isNavigationEnabled={isNavigationEnabled}
      productName={productName}
    >
      {children}
    </MenuCategoriesGrid.ProductCardLink>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  unavailableProductLabel: {
    defaultMessage: 'Currently unavailable',
    description: 'Menu | Product card | Unavailable product label',
  },
  quickAddToBagA11yLabel: {
    defaultMessage: 'Add 1 {product} to your bag',
    description: 'Menu | Product card | 1-click add to bag | a11y label',
  },
  lineageKeyCopied: {
    defaultMessage: 'Copied lineage key for {product_name}',
    description: 'Menu | Product card | Lineage key copied confirmation',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  notice: {
    marginTop: 6,
  },
  cardContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentProductCardProps = {
  product: MenuContentProduct;
  imageFetchingPriority: ComponentProps<typeof ProductCardV2.Image>['priority'];
  hoverImageUrl: string | undefined;
  hoverStaticImageUrl: string | undefined;
  hoverImageAlt: string | undefined;
  carousel: { fields: { imageVideo: Asset; staticImage?: Asset } }[] | undefined;
  generateHref: (productSlug: string) => Href;
  hasProductDetails: boolean;
  calorieOverride?: number;
  calorieOverrideText?: string;
  isLineageKeyCopyModeEnabled?: boolean;
  restrictionNotice: string | undefined;
  addProductToBag: ReturnType<typeof useOneClickAddToBag>['addProductToBag'];
  isAddingProductToBag: boolean;
  onCardPress: (product: MenuContentProduct) => void;
  onProductDetailsLinkPress: (product: MenuContentProduct) => void;
};

type MenuContentProductCardContainerProps = PropsWithChildren<{
  variation: 'link' | 'pressable';
  href: Href;
  onQuickAddToBag: () => void;
  onPress?: () => void;
  isLoading: boolean;
  isNavigationEnabled: boolean;
  productName: string;
}>;
